import { AlertTriangle, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";

export const fileUrl = (storagePath) => `${api.defaults.baseURL}/files/${storagePath}?auth=${localStorage.getItem("access_token") || ""}`;
const receiptUrl = (id) => `${api.defaults.baseURL}/movements/${id}/receipt?auth=${localStorage.getItem("access_token") || ""}`;

export function OverdueBanner({ overdue }) {
  const { t } = useLang();
  if (!overdue.length) return null;
  return (
    <div data-testid="overdue-banner" className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4">
      <div className="flex items-center gap-2 text-rose-200 font-semibold mb-2">
        <AlertTriangle className="w-4 h-4" />
        {overdue.length} {t("overdue_banner")}
      </div>
      <div className="flex flex-wrap gap-2">
        {overdue.map((m) => (
          <span key={m.id} data-testid={`overdue-item-${m.id}`} className="text-xs px-2.5 py-1 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-100">
            {m.equipment_name} ×{m.quantity} · {m.borrower_name || "?"} · {m.expected_return?.slice(0, 10)}
          </span>
        ))}
      </div>
    </div>
  );
}

export function EquipmentCard({ eq, late, onClick }) {
  const { t } = useLang();
  const outOfStock = eq.available === 0;
  return (
    <button
      onClick={onClick}
      data-testid={`equipment-card-${eq.id}`}
      className={`text-left glass rounded-2xl overflow-hidden hover:-translate-y-0.5 transition-transform ${late ? "ring-2 ring-rose-500/70" : ""}`}
    >
      <div className="aspect-video bg-white/5 relative">
        {eq.photo ? (
          <img src={fileUrl(eq.photo.storage_path)} alt={eq.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">—</div>
        )}
        {late && (
          <div className="absolute top-2 left-2">
            <Badge data-testid={`equipment-overdue-badge-${eq.id}`} className="bg-rose-600 text-white border-rose-400" variant="outline">
              <AlertTriangle className="w-3 h-3 mr-1" />{t("overdue")} ×{late}
            </Badge>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <Badge className={outOfStock ? "bg-rose-500/20 text-rose-300 border-rose-500/30" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"} variant="outline">
            {eq.available}/{eq.quantity}
          </Badge>
        </div>
      </div>
      <div className="p-4">
        <div className="font-semibold leading-tight">{eq.name}</div>
        <div className="text-xs text-slate-400 mt-0.5">{eq.category}</div>
      </div>
    </button>
  );
}

export function EquipmentGrid({ items, overdueByEq, onSelect }) {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {items.map((eq) => <EquipmentCard key={eq.id} eq={eq} late={overdueByEq[eq.id]} onClick={() => onSelect(eq)} />)}
      {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-16 text-sm">{t("no_data")}</div>}
    </div>
  );
}

function MovementRow({ m, canDelete, onDeleted }) {
  const { t } = useLang();
  const isOut = m.action === "out";
  const isLate = isOut && m.returned === false && m.expected_return && m.expected_return.slice(0, 10) < new Date().toISOString().slice(0, 10);
  const remove = async () => {
    try {
      const { data } = await api.delete(`/movements/${m.id}`);
      toast.success(data.restored_units ? `${t("movement_deleted")} · +${data.restored_units}` : t("movement_deleted"));
      onDeleted?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className={`glass rounded-lg p-3 text-sm ${isLate ? "border border-rose-500/50 bg-rose-500/5" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium">{m.equipment_name}</div>
        <div className="flex gap-1">
          {isLate && <Badge className="bg-rose-600 text-white border-rose-400" variant="outline">{t("overdue")}</Badge>}
          {isOut && m.returned && <Badge className="bg-white/5 text-slate-300 border-white/10" variant="outline">{t("returned")}</Badge>}
          <Badge className={isOut ? "bg-rose-500/20 text-rose-300 border-rose-500/30" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"} variant="outline">
            {isOut ? "SORTIE" : "ENTREE"} · {m.quantity}
          </Badge>
        </div>
      </div>
      <div className="text-xs text-slate-400 mt-1">
        {m.borrower_name} · {m.purpose} · {new Date(m.created_at).toLocaleString()}
        {m.expected_return && <span className={isLate ? "text-rose-300" : ""}> · {t("expected_return")}: {m.expected_return.slice(0, 10)}</span>}
      </div>
      {m.notes && <div className="text-xs text-slate-500 mt-1">{m.notes}</div>}
      {isOut && (
        <a data-testid={`movement-receipt-${m.id}`} href={receiptUrl(m.id)} target="_blank" rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-indigo-500/15 border border-indigo-400/30 text-indigo-200 hover:bg-indigo-500/25">
          <FileText className="w-3.5 h-3.5" />{t("receipt_pdf")}
        </a>
      )}
      {m.signature?.storage_path && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{t("signature")}</span>
          <img data-testid={`movement-signature-${m.id}`} src={fileUrl(m.signature.storage_path)} alt="signature" className="h-10 rounded bg-white/80 px-1" />
        </div>
      )}
      {canDelete && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" data-testid={`movement-delete-${m.id}`} className="mt-2 h-7 w-7 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" title={t("delete")} aria-label={t("delete")}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-slate-900 border-white/10" data-testid="movement-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("movement_delete_title")}</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                {t("movement_delete_hint")}
                <span className="block mt-2 text-slate-200">{m.equipment_name} ×{m.quantity} · {m.borrower_name || "—"}</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="movement-delete-cancel" className="bg-white/5 border-white/10">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="movement-delete-confirm" onClick={remove} className="bg-rose-500 hover:bg-rose-600">{t("delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export function MovementHistorySheet({ open, onOpenChange, history, canDelete, onChanged }) {
  const { t } = useLang();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-slate-900 border-white/10 w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle>{t("movement_history")}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2">
          {history.map((m) => <MovementRow key={m.id} m={m} canDelete={canDelete} onDeleted={onChanged} />)}
          {history.length === 0 && <div className="text-sm text-slate-500 text-center py-8">{t("no_data")}</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
